import type { WebGPURenderer } from 'three/webgpu';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';

import { Ktx2Loader } from './ktx2.js';
import { DECODER_BASIS, DECODER_DRACO } from './paths.js';

export interface Loaders {
  gltf: GLTFLoader;
  /** Standalone KTX2 textures (the decal atlas); the glTF loader shares the same instance. */
  ktx2: KTX2Loader;
  release(): void;
}

interface SharedLoaders {
  ktx2: KTX2Loader;
  draco: DRACOLoader;
  gltf: GLTFLoader;
  refs: number;
}

let shared: SharedLoaders | null = null;

/**
 * One KTX2/Draco/meshopt-capable GLTFLoader per page, reference counted: KTX2Loader warns when a
 * second instance is created while one is active (React StrictMode mounts twice in dev).
 * Call only after `await renderer.init()`; `detectSupport` reads the backend's capabilities.
 */
export function acquireLoaders(renderer: WebGPURenderer): Loaders {
  if (shared === null) {
    const ktx2 = new Ktx2Loader().setTranscoderPath(DECODER_BASIS).detectSupport(renderer);
    const draco = new DRACOLoader().setDecoderPath(DECODER_DRACO);
    const gltf = new GLTFLoader()
      .setKTX2Loader(ktx2)
      .setDRACOLoader(draco)
      .setMeshoptDecoder(MeshoptDecoder);
    shared = { ktx2, draco, gltf, refs: 0 };
  }
  const current = shared;
  current.refs++;
  let released = false;
  return {
    gltf: current.gltf,
    ktx2: current.ktx2,
    release() {
      if (released) return;
      released = true;
      current.refs--;
      if (current.refs === 0 && shared === current) {
        current.ktx2.dispose();
        current.draco.dispose();
        shared = null;
      }
    },
  };
}
